'use strict';

var CallsDevice = require('./util/CallsDevice');
var CmdDevice = require('./util/CmdDevice');
var log = require('../../../log/Log').channel('LifesizeIcon');

var maxCalls = 100;

// The driver maintains a calls object, indexed by the callId reported via the
// Icon API. Each call object contains the following properties. All of them
// (except configId) are copied directly from the call details or call state
// API events. The configId is used to map the Icon's call state to the
// driver's call interface that is being used to represent the call.
//
// - answeredTime: timestamp (in seconds) for the start of the call
// - callId: the Icon's call id
// - commConfId: not currently used
// - confUid: not currently used
// - direction: the call direction as reported by the Icon
// - name: the call name reported by the Icon
// - number: the call's number reported by the Icon
// - type: the call type reported by the Icon
// - state: the call state reported by the Icon
// - configId: the configId of the corresponding call interface

function LifesizeIcon(ipAddress, username, password, onConnectStatus, onControlStatus, config) {
    this.httpRequestBaseUrl = 'https://' + ipAddress;
    this.sessionId = null;

    CallsDevice.call(this);
    CmdDevice.call(this, ipAddress, username, password, onConnectStatus, onControlStatus, config);

    this.onCallStateChanged(this.callStateChangedHandler);
}

LifesizeIcon.prototype = Object.create(CmdDevice.prototype);
LifesizeIcon.prototype.constructor = LifesizeIcon;

Object.assign(LifesizeIcon.prototype, CallsDevice.prototype);

LifesizeIcon.prototype.name = 'LifesizeIcon';
LifesizeIcon.prototype.key = '-LttyZukV5cu-8P4RX44';
LifesizeIcon.prototype.httpRequestEnable = true;
LifesizeIcon.prototype.httpRequestBaseUrl = null; // assigned in constructor
LifesizeIcon.prototype.httpRequestDataFormat = 'json';
LifesizeIcon.prototype.pingAttemptLimit = 3;
LifesizeIcon.prototype.pingMs = 3000;
LifesizeIcon.prototype.pollIntervalMs = 3000;

LifesizeIcon.prototype.simulationParams = {
    caps: {
        // hasCalendar: true,
        // hasCameraTracking: true,
        hasFirmwareVersion: true,
        hasRoomMute: true,
        hasRoomVolume: true,
        hasVideoInputs: true,
        hasVideoSwitcher: true,
        maxCalls: {
            pstn: 0,
            video: 1,
            voip: 0,
        },
        numCameras: 1,
        videoInputsAreContent: true,
    },
    // callHasReceiveGain: function(ci, config, detected) {
    //     return false;
    // },
    configNotRequired: {
        roomMute: true,
        roomVolume: true,
    },
    // detected: not supported on LifesizeIcon
    firmwareVersion: 'LS_RM3_4.3.0 (2554)',
    // channelsAreEqual: not supported on LifesizeIcon
    // channelMatchesDetected: not supported on LifesizeIcon
    // getTelephonyType: not supported on LifesizeIcon
    // telephonyChannels: not supported on LifesizeIcon
};

LifesizeIcon.prototype.commands = {};

LifesizeIcon.prototype.commands.$buildversion = {};
LifesizeIcon.prototype.commands.$buildversion.activeStates = [ 'initializing' ];
LifesizeIcon.prototype.commands.$buildversion.activeInitializeStates = [ 'gettingFirmwareVersion' ];
LifesizeIcon.prototype.commands.$buildversion.get = function() {
    this.iconRequest('$buildversion', 'SysInfo_getInfo', { name: 'info.system.buildversion' });
};
LifesizeIcon.prototype.commands.$buildversion.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};
LifesizeIcon.prototype.commands.$buildversion.process = function(resp) {
    this.firmwareVersion = resp.value;
    this.doNextInitializeStep();
};

LifesizeIcon.prototype.commands.$calls = {};
LifesizeIcon.prototype.commands.$calls.activeStates = [ 'initializing', 'controlling' ];
LifesizeIcon.prototype.commands.$calls.activeInitializeStates = [ 'gettingCalls' ];
LifesizeIcon.prototype.commands.$calls.get = function() {
    this.iconRequest('$calls', 'Conf_getConferenceCallsByNatural', { confUid: '' });
};
LifesizeIcon.prototype.commands.$calls.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};
LifesizeIcon.prototype.commands.$calls.process = function(resp) {
    if (this.fsm.current === 'initializing') {
        this.lastCallId = null;

        resp.calls.callIds.forEach(function(callId) {
            if (callId === 0) return;
            this.lastCallId = callId;
            this.set('$callDetails', callId);
            this.set('$callState', callId);
        }, this);

        if (this.lastCallId === null) {
            this.cleanUpCallInterfaces(); // generates initial call interfaces
            this.doNextInitializeStep();
        }
    } else {
        // Get details and state for missing callIds.

        resp.calls.callIds.forEach(function(callId) {
            if (callId === 0 || this.calls.hasOwnProperty(callId)) return;
            this.set('$callDetails', callId);
            this.set('$callState', callId);
        }, this);
    }
};

LifesizeIcon.prototype.commands.$callDetails = {};
LifesizeIcon.prototype.commands.$callDetails.activeStates = [ 'initializing', 'controlling' ];
LifesizeIcon.prototype.commands.$callDetails.activeInitializeStates = [ 'gettingCalls' ];
LifesizeIcon.prototype.commands.$callDetails.set = function(callId) {
    this.iconRequest('$callDetails', 'Conf_getCallDetails', { callId: callId });
};
LifesizeIcon.prototype.commands.$callDetails.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};
LifesizeIcon.prototype.commands.$callDetails.process = function(resp) {
    this.updateCallDetails(resp.details);
};

LifesizeIcon.prototype.commands.$callState = {};
LifesizeIcon.prototype.commands.$callState.activeStates = [ 'initializing', 'controlling' ];
LifesizeIcon.prototype.commands.$callState.activeInitializeStates = [ 'gettingCalls' ];
LifesizeIcon.prototype.commands.$callState.set = function(callId) {
    this.iconRequest('$callState', 'Conf_getCallState', { callId: callId });
};
LifesizeIcon.prototype.commands.$callState.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};
LifesizeIcon.prototype.commands.$callState.process = function(resp) {
    this.updateCallState(resp.state);

    if (this.fsm.current === 'initializing' && resp.state.callId === this.lastCallId) {
        this.doNextInitializeStep();
    }
};

LifesizeIcon.prototype.commands.$checkSessionId = {};
LifesizeIcon.prototype.commands.$checkSessionId.activeStates = [ 'initializing' ];
LifesizeIcon.prototype.commands.$checkSessionId.activeInitializeStates = [ 'gettingSessionId' ];
LifesizeIcon.prototype.commands.$checkSessionId.get = function() {
    this.iconRequest('$checkSessionId', 'SysInfo_getInfo', { name: 'info.system.serialnumber' });
};
LifesizeIcon.prototype.commands.$checkSessionId.match = function(resp) {
    return resp;
};
LifesizeIcon.prototype.commands.$checkSessionId.process = function(resp) {
    if (resp._rv >= 0) {
        this.doNextInitializeStep();
    } else {
        this.query('$newSession');
    }
};

LifesizeIcon.prototype.commands.$listen = {};
LifesizeIcon.prototype.commands.$listen.activeStates = [ 'initializing' ];
LifesizeIcon.prototype.commands.$listen.activeInitializeStates = [ 'listening' ];
LifesizeIcon.prototype.commands.$listen.get = function() {
    this.iconRequest('$listen', 'listen', {
        events: [
            'Audio_muted',
            'Audio_volumeChanged',
            'Conf_callDetailsChanged',
            'Conf_callFailed',
            'Conf_callStateChanged',
            'Conf_dialCallDone',
        ]
    });
};
LifesizeIcon.prototype.commands.$listen.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};
LifesizeIcon.prototype.commands.$listen.process = function(resp) {
    this.doNextInitializeStep();
};

LifesizeIcon.prototype.commands.$longpoll = {};
LifesizeIcon.prototype.commands.$longpoll.isAsync = true;
LifesizeIcon.prototype.commands.$longpoll.receiveFullResponse = true;
LifesizeIcon.prototype.commands.$longpoll.get = function() {
    this.sendRequest('$longpoll', 'GET',
        '/longpoll.rb?session=' + this.sessionId + '&timeout=10', null, null, 11);
};
LifesizeIcon.prototype.commands.$longpoll.match = function(resp) {
    return resp;
};
LifesizeIcon.prototype.commands.$longpoll.process = function(resp) {
    if (resp.status === 1) {
        log.error(this + ': internal timeout on $longpoll');
    } else if (resp.status === 200) {
        var data = resp.data === '' ? [] : JSON.parse(resp.data);

        if (Array.isArray(data)) {
            for (var i = 0; i < data.length; i++) {
                var datum = data[i];
                if (this.longpollHandlers[datum.call]) {
                    this.longpollHandlers[datum.call].call(this, datum);
                } else {
                    log.error(this + ': no handler for longpoll datum = ' +
                        JSON.stringify(datum, null, 4));
                }
            }
        } else {
            log.error(this + ': unrecognized longpoll data = ' + JSON.stringify(data, null, 4));
        }
    } else {
        log.error(this +': unhandled status from longpoll, resp = ' +
            JSON.stringify(resp, null, 4));
    }

    this.query('$longpoll');
};

LifesizeIcon.prototype.commands.$mute = {};
LifesizeIcon.prototype.commands.$mute.activeStates = [ 'initializing', 'controlling' ];
LifesizeIcon.prototype.commands.$mute.activeInitializeStates = [ 'gettingMute' ];
LifesizeIcon.prototype.commands.$mute.get = function() {
    this.iconRequest('$mute', 'Audio_getMuteState');
};
LifesizeIcon.prototype.commands.$mute.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};
LifesizeIcon.prototype.commands.$mute.process = function(resp) {
    var mute = this.getNode(resp, 'state', 'unmuted') !== 'unmuted';

    if (this.mute !== mute) {
        this.mute = mute;
        if (this.fsm.current === 'controlling') this.broadcastStatus('mute', this.mute);
    }

    if (this.fsm.current === 'initializing') this.doNextInitializeStep();
};

LifesizeIcon.prototype.commands.$newSession = {};
LifesizeIcon.prototype.commands.$newSession.activeStates = [ 'initializing' ];
LifesizeIcon.prototype.commands.$newSession.activeInitializeStates = [ 'gettingSessionId' ];
LifesizeIcon.prototype.commands.$newSession.get = function() {
    this.sendRequest('$newSession', 'GET', '/rest/new', null, {
        Authorization: 'Basic ' + btoa(this.username + ':' + this.password),
    });
};
LifesizeIcon.prototype.commands.$newSession.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};
LifesizeIcon.prototype.commands.$newSession.process = function(resp) {
    this.sessionId = resp.session;
    this.doNextInitializeStep();
};

LifesizeIcon.prototype.commands.$remoteIr = {};
LifesizeIcon.prototype.commands.$remoteIr.set = function(button) {
    var buttons = { up: 0, down: 1, left: 2, right: 3, select: 4, mute: 5,
        volumeDown: 6, volumeUp: 7, zoomMinus: 8, zoomPlus: 9 };
    this.iconRequest('$remoteIr', 'IR_remoteIR', { button: buttons[button] });
};
LifesizeIcon.prototype.commands.$remoteIr.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};

LifesizeIcon.prototype.commands.$volume = {};
LifesizeIcon.prototype.commands.$volume.activeStates = [ 'initializing' ];
LifesizeIcon.prototype.commands.$volume.activeInitializeStates = [ 'gettingVolume' ];
LifesizeIcon.prototype.commands.$volume.get = function() {
    this.iconRequest('$volume', 'Audio_getVolume');
};
LifesizeIcon.prototype.commands.$volume.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};
LifesizeIcon.prototype.commands.$volume.process = function(resp) {
    this.volume = Math.round(0.50 * this.getNode(resp, 'volLevel', 0));
    this.doNextInitializeStep();
};

LifesizeIcon.prototype.commands.answer = {};
LifesizeIcon.prototype.commands.answer.set = function(callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error(this + ': answer: invalid callId ' + callId);
        return;
    }

    var call = this.getCallByConfigId(ci.configId);

    if (call === null) {
        log.error(this + ': answer: no call with configId ' + ci.configId);
        return;
    }

    this.iconRequest('answer', 'Conf_answer', { callId: call.callId });
};
LifesizeIcon.prototype.commands.answer.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};

LifesizeIcon.prototype.commands.call = {};
LifesizeIcon.prototype.commands.call.set = function(value) {
    var ci = this.getAvailableCallInterface(value.callType);

    if (ci === null) {
        log.error(this + ': call: no ' + value.callType + ' resources available');
        return;
    }

    if (typeof this.saveConfigId !== 'undefined') {
        log.error(this + ': saveConfigId already in use');
    }

    this.saveConfigId = ci.configId;

    this.placeCall(ci, value.callId, value.dialString);
    this.iconRequest('call', 'Conf_dialCall', {
        confUid: '', // use empty string for the default conference
        name: value.dialString,
        number: value.dialString,
        type: value.callType === 'video' ? 'video' : 'voice',
        protocol: '', // use empty string for default
        bitrate: 0, // use 0 for the conference's value
        from: '', // use empty string for default
        transId: ci.configId,
    });
};
LifesizeIcon.prototype.commands.call.match = function(resp) {
    return resp;
};
LifesizeIcon.prototype.commands.call.process = function(resp) {
    if (resp.returnValue < 0) {
        log.error(this + ': unable to place call');
        var ci = this.getCallInterfaceByConfigId(resp.transId);

        if (ci !== null) {
            this.disconnectCall(ci);
        }

        return;
    }

    if (!this.calls.hasOwnProperty(resp.callId)) {
        this.calls[resp.callId] = { callId: resp.callId };
    }

    this.calls[resp.callId].configId = resp.transId;
};

LifesizeIcon.prototype.commands.callStatus = {};
LifesizeIcon.prototype.commands.callStatus.get = function() {
    this.sendCallStatusMessages();
};

LifesizeIcon.prototype.commands.dial = {};
LifesizeIcon.prototype.commands.dial.set = function(value) {
    var ci = this.getCallInterfaceByCallId(value.callId);

    if (ci === null) {
        log.error(this + ': dial: invalid callId ' + value.callId);
        return;
    }

    this.dialCall(ci, value.dialString);
    this.iconRequest('dial', 'Auto_callDtmfSend', { digit: value.dialString });
};
LifesizeIcon.prototype.commands.dial.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};

LifesizeIcon.prototype.commands.firmwareVersion = {};
LifesizeIcon.prototype.commands.firmwareVersion.get = function() {
    this.broadcastStatus('firmwareVersion', this.firmwareVersion);
};

LifesizeIcon.prototype.commands.hangup = {};
LifesizeIcon.prototype.commands.hangup.set = function(callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error(this + ': hangup: invalid callId ' + callId);
        return;
    }

    var call = this.getCallByConfigId(ci.configId);

    if (call === null) {
        log.error(this + ': hangup: no call with configId ' + ci.configId);
        return;
    }

    this.endCall(ci);
    this.iconRequest('hangup', 'Conf_terminate', { callId: call.callId });
};
LifesizeIcon.prototype.commands.hangup.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};

LifesizeIcon.prototype.commands.mute = {};
LifesizeIcon.prototype.commands.mute.get = function() {
    this.broadcastStatus('mute', this.mute);
};
LifesizeIcon.prototype.commands.mute.set = function(value) {
    this.mute = value;
    this.broadcastStatus('mute', this.mute);

    this.removeRequest('mute');

    if (this.mute) {
        this.iconRequest('mute', 'Audio_mute', {
            inChoice: 0, // docs say unused, but device requires it
        });
    } else {
        this.iconRequest('mute', 'Audio_unmute');
    }
};
LifesizeIcon.prototype.commands.mute.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};

LifesizeIcon.prototype.commands.ping = {};
LifesizeIcon.prototype.commands.ping.activeStates = [ 'controlling', 'controlFailed' ];
LifesizeIcon.prototype.commands.ping.get = function() {
    this.iconRequest('ping', 'SysInfo_getInfo', { name: 'info.system.serialnumber' });
};
LifesizeIcon.prototype.commands.ping.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};

LifesizeIcon.prototype.commands.reject = {};
LifesizeIcon.prototype.commands.reject.set = function(callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error(this + ': reject: invalid callId ' + callId);
        return;
    }

    var call = this.getCallByConfigId(ci.configId);

    if (call === null) {
        log.error(this + ': reject: no call with configId ' + ci.configId);
        return;
    }

    this.endCall(ci);
    this.iconRequest('reject', 'Conf_reject', { callId: call.callId });

};
LifesizeIcon.prototype.commands.reject.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};

LifesizeIcon.prototype.commands.volume = {};
LifesizeIcon.prototype.commands.volume.get = function() {
    this.broadcastStatus('volume', this.volume);
};
LifesizeIcon.prototype.commands.volume.set = function(value) {
    this.volume = value;
    this.broadcastStatus('volume', this.volume);
    this.removeRequest('volume');
    this.iconRequest('volume', 'Audio_setVolume', { volLevel: this.volume * 4 });
};
LifesizeIcon.prototype.commands.volume.match = function(resp) {
    return resp && resp._rv >= 0 ? resp : null;
};

LifesizeIcon.prototype.callStateChangedHandler = function(ci, prevCallState) {
    var callState = this.getCallState(ci);

    if (callState === 'disconnected') {
        this.cleanUpCallInterfaces();

        var foundCall = null;

        for (var iconCallId in this.calls) {
            if (!this.calls.hasOwnProperty(iconCallId)) continue;
            var call = this.calls[iconCallId];
            if (call.configId === ci.configId) {
                foundCall = call;
                break;
            }
        }

        if (foundCall) delete this.calls[foundCall.callId];
    }
};

LifesizeIcon.prototype.cleanUpCallInterfaces = function() {
    var availableIds = [ 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19 ];
    var representedCallTypes = {};
    var ci;
    var i;

    // Remove any disconnected call interfaces.

    for (i = 0; i < this.callInterfaces.length; i++) {
        ci = this.callInterfaces[i];

        if (this.getCallState(ci) === 'disconnected') {
            this.callInterfaces.splice(i--, 1);
        }
    }

    // Add call interfaces as necessary.

    for (i = 0; i < this.callInterfaces.length; i++) {
        ci = this.callInterfaces[i];

        if (!representedCallTypes.hasOwnProperty(ci.callType)) {
            representedCallTypes[ci.callType] = true;
        }

        var idx = availableIds.indexOf(parseInt(ci.configId.substring(1), 10));
        if (idx >= 0) availableIds.splice(idx, 1);
    }

    [ 'video', 'voip' ].forEach(function(callType) {
        if (callType === 'voip' && (!this.sipEnabled || !this.config.audioDialingEnabled)) return;

        if (!representedCallTypes[callType]) {
            i = availableIds.shift();

            if (typeof i !== 'undefined') {
                this.callInterfaces.push({
                    configId: 'a' + i,
                    name: 'a' + i,
                    callType: callType,
                });
            } else {
                log.error(this + ': out of available ids; can\'t add ' + callType + 'interface');
            }
        }
    }, this);

    if (this.fsm.current === 'controlling') this.computeCaps();
};

LifesizeIcon.prototype.getCallByConfigId = function(configId) {
    for (var iconCallId in this.calls) {
        if (!this.calls.hasOwnProperty(iconCallId)) continue;
        var call = this.calls[iconCallId];
        if (call.configId === configId) return call;
    }

    return null;
};

LifesizeIcon.prototype.iconRequest = function(param, call, params) {
    var data = { call: call };
    if (typeof params !== 'undefined' && params !== null) data.params = params;
    this.sendRequest(param, 'POST', '/rest/request/' + this.sessionId, data);
};

LifesizeIcon.prototype.initializeSteps = [{
    state: 'gettingSessionId',
    action: function() {
        if (this.sessionId) {
            this.query('$checkSessionId');
        } else {
            this.query('$newSession');
        }

        return true;
    }
}, {
    state: 'gettingFirmwareVersion',
    action: '$buildversion',
}, {
    state: 'gettingCalls',
    action: '$calls',
}, {
    state: 'gettingMute',
    action: '$mute',
}, {
    state: 'gettingVolume',
    action: '$volume',
}, {
    state: 'listening',
    action: '$listen',
}, {
    state: 'finishInitialization',
    action: function() {
        this.finishInitialization();
        return true;
    },
}];

LifesizeIcon.prototype.getNextConfigId = function() {
    var configId = null;

    for (var i = 0; i < maxCalls; i++) {
        var tmp = 'a' + i;
        var conflict = false;

        for (var j = 0; i < this.callInterfaces.length; j++) {

            if (tmp === this.callInterfaces[j].configId) {
                conflict = true;
                break;
            }
        }

        if (!conflict) {
            configId = tmp;
            break;
        }
    }

    return configId;
};

LifesizeIcon.prototype.longpollHandlers = {};

LifesizeIcon.prototype.longpollHandlers.Audio_muted = function(data) {
    var value = this.getNode(data, 'params.status', 0) === 1;

    if (this.mute !== value) {
        this.mute = value;
        if (this.fsm.current === 'controlling') this.broadcastStatus('mute', this.mute);
    }
};

LifesizeIcon.prototype.longpollHandlers.Audio_volumeChanged = function(data) {
    var value = Math.round(0.50 * this.getNode(data, 'params.volLevel', 0));

    if (this.volume !== value) {
        this.volume = value;
        if (this.fsm.current === 'controlling') this.broadcastStatus('volume', this.volume);
    }
};

LifesizeIcon.prototype.longpollHandlers.Conf_callDetailsChanged = function(data) {
    this.updateCallDetails(data.params.details);
    if (!this.hasRequest('$mute')) this.query('$mute');
};

LifesizeIcon.prototype.longpollHandlers.Conf_callFailed = function(data) {
    this.defer((function() {
        this.set('$remoteIr', 'select');
    }).bind(this), 2000);
};

LifesizeIcon.prototype.longpollHandlers.Conf_callStateChanged = function(data) {
    this.updateCallState(this.getNode(data, 'params.state', {}));
    if (!this.hasRequest('$mute')) this.query('$mute');
};

LifesizeIcon.prototype.longpollHandlers.Conf_dialCallDone = function(data) {
    // This arrives after the call details and call state for an outgoing call,
    // so the transId isn't useful because it is too late.
};

LifesizeIcon.prototype.onComputeCaps = function() {
    var caps = this.getCapsTemplate();
    caps.hasFirmwareVersion = true;
    caps.hasRoomMute = true;
    caps.hasRoomVolume = true;
    caps.hasZoomConnector = this.config.zoomConnectorEnabled === true;
    this.computeCapsMaxCalls(caps);
    return caps;
};

LifesizeIcon.prototype.onControl = function() {
    this.query('$longpoll');
    this.finishControlling();
};

LifesizeIcon.prototype.onInitialize = function() {
    this.busy = false;
    this.callInterfaces.length = 0; // populated in one of the initialize steps
    this.calls = {};
    this.firmwareVersion = null;
    this.lastCameraPreset = null;
    this.mute = true;
    this.pollCycle = 1000;
    this.volume = 0;

    this.startInitializer();
};

// Poll the call information. We do this to double-check that our state stays
// in sync with the device -- it's possible that a message got missed or a bug
// resulted in the wrong state

LifesizeIcon.prototype.onPoll = function() {
    var activeCallInterfaces = [];

    this.callInterfaces.forEach(function(ci) {
        if (this.getCallState(ci) !== 'disconnected') activeCallInterfaces.push(ci);
    }, this);

    if (++this.pollCycle > activeCallInterfaces.length) this.pollCycle = 0;

    if (this.pollCycle === activeCallInterfaces.length) {
        if (!this.hasRequest('$calls')) this.query('$calls');
    } else {
        var ci = activeCallInterfaces[this.pollCycle];

        if (this.getCallState(ci) !== 'disconnected') {
            var call = this.getCallByConfigId(ci.configId);

            if (call === null) {
                log.error(this + ': no call with configId ' + ci.configId);
                return;
            }

            if (!call.hasOwnProperty('callId')) {
                log.error(this + ': no callId for call = ' + JSON.stringify(call, null, 4));
                return;
            }

            var iconCallId = call.callId;
            this.set('$callDetails', iconCallId);
            this.set('$callState', iconCallId);
        }
    }
};

LifesizeIcon.prototype.onUpdateConfig = function() {
    // Don't call CallsDevice.onUpdateConfig(this); we are manually defining
    // our own callInterfaces.
};

LifesizeIcon.prototype.translateCallState = function(call, unknown) {
    if (typeof unknown === 'undefined') unknown = null;

    switch (call.state) {
        case 'dialing':
        case 'incoming':
        case 'ringing':
            return 'ringing';
        case 'answered':
        case 'media-connected':
            return 'connected';
        case 'terminating':
            return 'disconnecting';
        case 'terminated':
            return 'disconnected';
        default:
            break;
    }

    log.error(this + ': unhandled call.state, call = ' + JSON.stringify(call, null, 4));
    return unknown;
};

LifesizeIcon.prototype.updateCallDetails = function(details) {
    if (typeof details.callId === 'undefined' || details.callId === null) {
        log.error(this + ': missing callId, details = ' + JSON.stringify(details, null, 4));
        return;
    }

    if (!this.calls.hasOwnProperty(details.callId)) {
        this.calls[details.callId] = {};
    }

    var call = this.calls[details.callId];
    var prevCallType = call.type;

    call.answeredTime = this.getNode(details, 'answeredTime', Date.now());
    call.callId = details.callId;
    call.commConfId = this.getNode(details, 'commConfId');
    call.confUid = this.getNode(details, 'confUid');
    call.direction = this.getNode(details, 'direction', 'outgoing');
    call.name = this.getNode(details, 'name');
    call.number = this.getNode(details, 'number');
    call.type = this.getNode(details, 'type', 'video');

    if (call.direction === 'outgoing' && typeof this.saveConfigId !== 'undefined') {
        call.configId = this.saveConfigId;
        delete this.saveConfigId;
    }

    var iconCallState = this.getNode(call, 'state', 'terminated');
    var configId = this.getNode(call, 'configId');

    if (iconCallState === 'terminated') return;

    if (configId === null) return null; // the call interface hasn't been assigned yet

    var ci = this.getCallInterfaceByConfigId(configId);

    if (ci === null) {
        log.error(this + ': no call interface with configId for call = ' +
            JSON.stringify(call, null, 4));
        return;
    }

    var number; // undefined if no update
    var displayDialString; // undefined if no update

    if (call.direction !== 'outgoing' || this.getNode(ci, 'callStatus.dialString') === null) {
        number = call.number;
    }

    var needsBroadcast = false;

    // Incoming calls start with type 'none', which we treat as 'voip', then
    // they later transition to 'video' or 'voice'.

    if (call.type !== prevCallType) {
        this.setNode(ci, 'callStatus.callType', call.type === 'video' ? 'video' : 'voip');
        needsBroadcast |= true;
    }

    needsBroadcast |= this.updateCallerId(ci, number, call.name, displayDialString);

    if (needsBroadcast && this.fsm.current === 'controlling') {
        this.broadcastStatus('callStatus', JSON.parse(JSON.stringify(ci.callStatus)));
    }
};

LifesizeIcon.prototype.updateCallState = function(state, init) {
    if (typeof init === 'undefined') init = false;

    var iconCallId = this.getNode(state, 'callId');

    if (iconCallId === null) {
        log.error(this + ': missing callId, state = ' + JSON.stringify(state, null, 4));
        return;
    }

    if (!this.calls.hasOwnProperty(iconCallId)) {
        log.error(this + ': no call with callId: ' + iconCallId);
        return;
    }

    var call = this.calls[iconCallId];
    var prevState = call.state ? this.translateCallState(call) : null;
    call.state = this.getNode(state, 'state');
    var newState = this.translateCallState(call);

    if (prevState === newState) return;

    // If the Icon is in Do Not Disturb, then incoming calls will appear in the
    // "terminating" (disconnecting) and then "terminated" (disconnected)
    // states. Just ignore them.

    if (prevState === null && call.direction === 'incoming' &&
        (newState === 'disconnecting' || newState === 'disconnected')) return;

    // We probably don't need this here; it should get handled in
    // updateCallDetails as long as that event arrives first.

    if (call.direction === 'outgoing' && typeof this.saveConfigId !== 'undefined') {
        call.configId = this.saveConfigId;
        delete this.saveConfigId;
    }

    var callType = call.callType === 'video' ? 'video' : 'voip';
    var ci;

    if (!call.hasOwnProperty('configId')) {
        ci = this.getAvailableCallInterface(callType);

        if (ci === null) {
            var configId = this.getNextConfigId();

            if (configId === null) {
                log.error(this + ': exceeded maxCalls = ' + maxCalls);
                return;
            }

            call.configId = configId;
            ci = {
                configId: configId,
                name: configId,
                callType: callType,
            };

            this.callInterfaces.push(ci);
            if (this.fsm.current === 'controlling') this.computeCaps();
        }
    } else {
        ci = this.getCallInterfaceByConfigId(call.configId);

        if (ci === null) {
            log.error(this + ': no call interface with configId = ' + call.configId);
            return;
        }
    }

    var dialString; // leave undefined if already set

    if (this.getNode(ci, 'callStatus.dialString') === null) {
        dialString = call.number;
    }

    switch (newState) {
        case 'ringing':
            if (call.direction === 'outgoing') {
                this.placeCall(ci, null, dialString, call.name);
            } else {
                this.setIncomingCall(ci, dialString, call.name);
            }

            break;
        case 'connected':
            this.connectCall(ci, null, dialString, call.name);
            break;
        case 'disconnecting':
            this.endCall(ci);
            break;
        case 'disconnected':
            this.disconnectCall(ci);
            delete call.configId;
            break;
        default:
            log.error(this + ': unhandled call state: ' + newState);
            break;
    }

    if (init && newState !== 'disconnected') {
        this.setNode(ci, 'callStatus.direction', call.direction);
        this.setNode(ci, 'callStatus.start', call.answeredTime * 1000);
    }
};

module.exports = LifesizeIcon;
